import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { LoginHeaderHelperComponent } from './login-header-helper.component';
import {
  LoggedInDetails,
  SignupLoginContent,
  ToolTipToggle
} from './models/login-header.component.interface';

@Component({
  selector: 'idp-login',
  templateUrl: './login-header.component.html',
  styleUrls: ['./login-header.component.scss'],
  standalone: true,
  imports: [CommonModule],
  encapsulation: ViewEncapsulation.None
})
export class LoginHeaderComponent
  extends LoginHeaderHelperComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input() signupLoginContent: SignupLoginContent = {} as SignupLoginContent;
  @Input() loggedInDetails: LoggedInDetails = {} as LoggedInDetails;
  @Input() isLoggedIn = false;
  @Input() isWarmState = false;
  @Input() isReady = false;
  @Input() flyoutToggle = false;
  @Input() toolTipToggles: ToolTipToggle = {} as ToolTipToggle;
  @Input() shopHeader = false;
  @Input() override isMobile = false;
  @Output() closeSignupToolTipPopup = new EventEmitter<Event>();
  @Output() closeLoginToolTipPopup = new EventEmitter<Event>();
  @Output() closeAccountRecoveryPopup = new EventEmitter<Event>();
  @Output() accountRecoveryLinkClick = new EventEmitter<void>();
  @Output() toggleLoginModal = new EventEmitter<boolean>();
  @Output() openFlyout = new EventEmitter<Event>();
  @Output() openFlyoutTracking = new EventEmitter<HTMLElement>();
  @Output() analyticsEvent = new EventEmitter<HTMLElement>();

  ngAfterViewInit(): void {
    this.initializeTooltips();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.shouldUpdateTooltips(changes)) {
      this.updateTooltipConditions();
      this.initializeTooltips();
      this.handleMobileAutoClose();
    }
    if (changes['isMobile']) {
      this.clearAutoClose();
      this.closeAllTooltips();
      this.initializeTooltips();
      this.signupCondition = false;
    }
  }

  private shouldUpdateTooltips(changes: SimpleChanges): boolean {
    return !!(changes['isReady'] || changes['toolTipToggles']);
  }

  private updateTooltipConditions(): void {
    const tooltipContent = this.signupLoginContent?.toolTipContent;
    this.signupCondition =
      this.getSignUpCondition() &&
      this.hasTooltipContent(tooltipContent?.tooltipSignUpContent);
    this.loginCondition =
      this.getLoginCondition() &&
      this.hasTooltipContent(tooltipContent?.tooltipLoginContent);
    this.accountRecoveryCondition =
      this.getAccountRecoveryCondition() &&
      this.hasAccountRecoveryContent(
        this.signupLoginContent?.toolTipContent?.tooltipAccountRecoveryContent
      );
  }

  private getSignUpCondition(): boolean {
    return (
      this.isReady &&
      !this.isLoggedIn &&
      !this.isWarmState &&
      this.toolTipToggles.displaySignUpToolTip
    );
  }

  private getLoginCondition(): boolean {
    return (
      this.toolTipToggles.isToolTipisHidden == false &&
      this.toolTipToggles.displayLoginToolTip
    );
  }

  private getAccountRecoveryCondition(): boolean {
    return this.isLoggedIn && this.toolTipToggles.displayAccountRecoveryToolTip;
  }

  closeSignupTooltip(event?: Event): void {
    this.handleTooltipClose('signup', event);
    this.closeSignupToolTipPopup.emit();
  }

  closeLoginTooltip(event?: Event): void {
    this.handleTooltipClose('login', event);
    this.closeLoginToolTipPopup.emit();
  }

  closeAccountRecoveryTooltip(event?: Event): void {
    this.handleTooltipClose('accountRecovery', event);
    this.closeAccountRecoveryPopup.emit();
  }

  onAccountRecoveryLinkClick(): void {
    this.accountRecoveryLinkClick.emit();
  }

  toggleModal(): void {
    this.toggleLoginModal.emit();
  }

  open(event: Event): void {
    const element = event.currentTarget as HTMLElement;
    this.openFlyoutTracking.emit(element);
    this.openFlyout.emit(event);
  }

  openLoggedInFlyout(event: KeyboardEvent): void {
    if (event.key === 'ENTER') {
      this.openFlyout.emit(event);
    }
  }

  analyticsEventEmit(event: Event): void {
    const element = event.currentTarget as HTMLElement;
    this.analyticsEvent.emit(element);
  }
}
